import _ from "lodash";
import { Dispatch, SetStateAction, useCallback, useState } from "react";

import { IFilter } from "@/types/filter.types";

export type UseFiltersReturn<T> = {
  filters: IFilter<T>[];
  temporaryFilters: IFilter<T>[];
  addNewTemporaryFilter: (filter: IFilter<T>) => void;
  deleteTemporaryFilter: (index: number) => void;
  submitTemporaryFilters: () => void;
  clearTemporaryFilters: () => void;
  addNewFilter: (filter: IFilter<T>) => void;
  deleteFilter: (filter: IFilter<T>) => void;
  changeFilters: (filters: IFilter<T>[]) => void;
  clearFilters: () => void;
  setTemporaryFilters: Dispatch<SetStateAction<IFilter<T>[]>>;
};

export type UseFiltersProps<T> = {
  initialValue: IFilter<T>[];
};

const useFilters = <T>(props?: UseFiltersProps<T>): UseFiltersReturn<T> => {
  const { initialValue } = props || {};
  const [filters, setFilters] = useState<IFilter<T>[]>(initialValue || []);
  const [temporaryFilters, setTemporaryFilters] = useState<IFilter<T>[]>([]);

  const addNewTemporaryFilter = (filter: IFilter<T>) => {
    setTemporaryFilters((previousFilters) => [filter, ...previousFilters]);
  };

  const deleteTemporaryFilter = (index: number) => {
    setTemporaryFilters((currentFilters) => currentFilters.filter((_, idx) => idx !== index));
  };

  const submitTemporaryFilters = () => {
    setFilters(temporaryFilters);
    setTemporaryFilters([]);
  };

  const clearTemporaryFilters = () => {
    setTemporaryFilters([]);
  };

  const addNewFilter = (filter: IFilter<T>) => {
    setFilters((previousFilters) => [filter, ...previousFilters]);
  };

  const deleteFilter = (filter: IFilter<T>) => {
    const newFilters = filters.filter((item) => !_.isEqual(filter, item));
    setFilters(newFilters);
  };

  const changeFilters = useCallback((newFilters: IFilter<T>[]) => {
    setFilters(newFilters);
  }, []);

  const clearFilters = () => {
    setFilters([]);
  };

  return {
    filters,
    temporaryFilters,
    addNewTemporaryFilter,
    deleteTemporaryFilter,
    submitTemporaryFilters,
    clearTemporaryFilters,
    setTemporaryFilters,
    addNewFilter,
    deleteFilter,
    changeFilters,
    clearFilters,
  };
};

export default useFilters;
